import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import { listCacheFallback } from '@/lib/list-cache-fallback';
import type {
  SalesDocument,
  SalesDocumentFilters,
  CreateSalesDocumentInput,
  UpdateSalesDocumentInput,
  SalesDocType,
} from '@erp/data-access/interfaces';

const SALES_KEY = ['sales'] as const;
const OFFLINE_PREFIX = 'OFFLINE-';

/**
 * Always fetches the FULL document list (one canonical key
 * `['sales', undefined]` populated by usePrewarmCache) and filters in
 * memory. Every call site — list pages, dashboard widgets, client/rep
 * profiles — gets the same cached array and a JS-only filter pass.
 * Net result: every page that needs sales docs works offline without
 * its own network round-trip.
 */
export function useSalesDocuments(filters?: SalesDocumentFilters) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...SALES_KEY, filters],
    queryFn: () => ds.sales.listDocuments(filters),
    placeholderData: keepPreviousData,
  });
}

export function useSalesDocumentCount(filters?: SalesDocumentFilters) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...SALES_KEY, 'count', filters],
    queryFn: () => ds.sales.countDocuments(filters),
    placeholderData: keepPreviousData,
  });
}

export function useSalesDocument(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...SALES_KEY, id],
    queryFn: () => ds.sales.getDocumentById(id),
    enabled: !!id,
    placeholderData: () => listCacheFallback<SalesDocument>(qc, SALES_KEY, id),
  });
}

/**
 * Optimistic skeleton for a sales document. Sequential numbering is
 * server-authoritative so we use OFFLINE-XXX as a placeholder; once the
 * sync engine drains the outbox the real number lands and TanStack
 * Query refreshes the row.
 *
 * We don't try to recompute totals here — the form already runs the
 * calculation via libs/domain/sales and ships the result inside `input`.
 */
function optimisticSalesDocument(input: CreateSalesDocumentInput, clientId: string): SalesDocument {
  return {
    id: clientId,
    docType: input.docType,
    number: OFFLINE_PREFIX + clientId.slice(0, 6).toUpperCase(),
    clientId: input.clientId,
    representativeId: input.representativeId ?? null,
    carRegistrationNumber: input.carRegistrationNumber ?? null,
    docDate: input.docDate ?? new Date().toISOString().slice(0, 10),
    validityDate: null,
    parentDocId: input.parentDocId ?? null,
    isInvoiced: false,
    notes: input.notes ?? null,
    totalHt: input.totalHt ?? 0,
    totalVat: input.totalVat ?? 0,
    totalTtc: input.totalTtc ?? 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  } as unknown as SalesDocument;
}

export function useCreateSalesDocument() {
  const ds = useDataService();
  return useResilientMutation<CreateSalesDocumentInput, SalesDocument>({
    entity: 'sales_documents',
    op: 'create',
    invalidate: [SALES_KEY],
    timeoutMs: 20000,
    online: (input) => ds.sales.createDocument(input),
    optimistic: optimisticSalesDocument,
    applyOptimistic: (qc, doc) => {
      qc.setQueriesData<SalesDocument[]>({ queryKey: SALES_KEY }, (old) =>
        Array.isArray(old) && !old.some((d) => d.id === doc.id) ? [doc, ...old] : old,
      );
    },
  });
}

export function useUpdateSalesDocument() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useResilientMutation<{ id: string; input: UpdateSalesDocumentInput }, SalesDocument>({
    entity: 'sales_documents',
    op: 'update',
    invalidate: [SALES_KEY],
    timeoutMs: 20000,
    online: ({ id, input }) => ds.sales.updateDocument(id, input),
    onSuccess: (doc) => {
      if (doc.proCommandeCode) {
        // A Pro-linked BL save also updates the Pro order server-side.
        // Refresh those caches so returning to the order page does not show
        // the pre-save totals/notes from TanStack Query.
        qc.invalidateQueries({ queryKey: ['admin-pro-commande', doc.proCommandeCode] });
        qc.invalidateQueries({ queryKey: ['admin-pro-commandes'] });
      }
    },
    optimistic: ({ id, input }) =>
      ({
        id,
        ...input,
        updatedAt: new Date().toISOString(),
      }) as unknown as SalesDocument,
    applyOptimistic: (qc, _r, { id, input }) => {
      qc.setQueriesData<SalesDocument[]>({ queryKey: SALES_KEY }, (old) =>
        Array.isArray(old)
          ? old.map((d) => (d.id === id ? ({ ...d, ...input } as unknown as SalesDocument) : d))
          : old,
      );
    },
  });
}

export function useDeleteSalesDocument() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'sales_documents',
    op: 'delete',
    invalidate: [SALES_KEY],
    online: (id) => ds.sales.deleteDocument(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, id) => {
      qc.setQueriesData<SalesDocument[]>({ queryKey: SALES_KEY }, (old) =>
        Array.isArray(old) ? old.filter((d) => d.id !== id) : old,
      );
    },
  });
}

// Sequential numbering MUST be server-authoritative — query is online-only.
// When offline, the optimistic doc shows OFFLINE-XXX until sync confirms
// the real number from the server's sequence.
export function useNextSalesNumber(docType: SalesDocType) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...SALES_KEY, 'next-number', docType],
    queryFn: () => ds.sales.getNextNumber(docType),
  });
}

export function useMarkAsInvoiced() {
  const ds = useDataService();
  return useResilientMutation<string, SalesDocument>({
    entity: 'sales_documents',
    op: 'custom',
    method: 'markAsInvoiced',
    invalidate: [SALES_KEY],
    online: (id) => ds.sales.markAsInvoiced(id) as unknown as Promise<SalesDocument>,
    optimistic: (id) => ({ id, isInvoiced: true } as unknown as SalesDocument),
    applyOptimistic: (qc, _r, id) => {
      qc.setQueriesData<SalesDocument[]>({ queryKey: SALES_KEY }, (old) =>
        Array.isArray(old) ? old.map((d) => (d.id === id ? { ...d, isInvoiced: true } : d)) : old,
      );
    },
  });
}

export function useChildDocuments(parentId: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...SALES_KEY, 'children', parentId],
    queryFn: () => ds.sales.getChildDocuments(parentId),
    enabled: !!parentId,
  });
}
